import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Eye,
  FileText,
  Loader2,
  ShieldCheck,
  Trash2,
  UploadCloud
} from 'lucide-react';
import { DepotLayout } from './DepotLayout';
import {
  DepotDocumentPreview,
  type DocumentPreviewState
} from './DepotDocumentPreview';
import {
  DEPOT_MAX_FILE_SIZE_BYTES,
  DepotBusinessError,
  formatDepotErrorMessage
} from '../api/depotSecuriseApi';
import type {
  DepotBrandingDto,
  DepotSecuriseInfosResponse,
  DepotUploadedDocument
} from '../types';

interface Props {
  infos: DepotSecuriseInfosResponse;
  /**
   * Vrai pendant l'appel /confirmer. Tant qu'il y a au moins un upload en
   * cours OU une confirmation en cours, on bloque le bouton final.
   */
  isConfirming: boolean;
  /** Erreur métier renvoyée par /confirmer (ex DDN invalidée entre-temps). */
  confirmErrorMessage?: string;
  /**
   * Délégué d'upload. Le back renvoie la liste fraîche dans `infos`, donc
   * cette callback ne sert qu'à déclencher l'appel — la page parente fait
   * l'update du state. Le message optionnel retourné est purement cosmétique.
   */
  onUploadFile: (file: File) => Promise<{ message?: string }>;
  /** Délégué de suppression d'un fichier déjà déposé. */
  onDeleteFile: (storedFileId: string) => Promise<void>;
  /**
   * Délégué de prévisualisation. La page gère les erreurs terminales
   * (token bloqué, DDN invalidée → bascule écran erreur). Cette zone ne
   * voit que les erreurs récupérables qu'elle affiche dans la modale.
   */
  onPreviewFile: (
    storedFileId: string
  ) => Promise<{ blob: Blob; contentType: string }>;
  /** Click sur "J'ai terminé". */
  onConfirm: () => void;
  /** Habillage courtier/produit. */
  branding?: DepotBrandingDto;
}

/**
 * Suivi local d'un fichier en cours d'upload ou rejeté. La source de vérité
 * pour les fichiers ACCEPTÉS est `infos.uploadedDocuments` côté back ; on ne
 * garde ici que ce qui n'est PAS encore (ou jamais) là-bas.
 */
type LocalUploadStatus =
  | { state: 'uploading' }
  | { state: 'error'; message: string };

interface LocalUploadEntry {
  id: string;
  fileName: string;
  size: number;
  status: LocalUploadStatus;
}

const MAX_FILE_SIZE_LABEL = `${Math.round(
  DEPOT_MAX_FILE_SIZE_BYTES / (1024 * 1024)
)} Mo`;

/**
 * Formats autorisés côté back (cf. contrat API) : PDF, JPG, JPEG, PNG.
 * On les expose à l'input pour limiter la sélection système, mais le back
 * fait sa propre validation magic bytes — un bidouillage d'extension ne
 * passe pas en prod.
 */
const ACCEPTED_FORMATS = '.pdf,.jpg,.jpeg,.png';

/** Génère un id stable côté client pour mapper React keys aux entrées. */
let uploadIdSeq = 0;
const nextUploadId = () => `upload-${++uploadIdSeq}`;

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

const formatUploadedAt = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const DepotUploadZone: React.FC<Props> = ({
  infos,
  isConfirming,
  confirmErrorMessage,
  onUploadFile,
  onDeleteFile,
  onPreviewFile,
  onConfirm,
  branding
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  /**
   * Liste éphémère des uploads en cours / rejetés. Les uploads acceptés
   * sortent d'ici dès que la liste back est rafraîchie (le fichier
   * apparaît alors dans `infos.uploadedDocuments`).
   */
  const [localUploads, setLocalUploads] = useState<LocalUploadEntry[]>([]);
  /**
   * Ids des fichiers en cours de suppression — pour griser le bouton et
   * afficher un spinner sur la ligne concernée.
   */
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  /**
   * Aperçu en cours : { doc, state } ou null. On garde l'objectUrl dans
   * le state pour pouvoir le révoquer proprement à la fermeture.
   */
  const [preview, setPreview] = useState<{
    doc: DepotUploadedDocument;
    state: DocumentPreviewState;
  } | null>(null);

  /**
   * Garantit qu'on ne laisse jamais traîner un blob URL en mémoire :
   * révoque celui en cours quand le composant disparaît ou quand on
   * change d'aperçu.
   */
  useEffect(() => {
    return () => {
      if (preview?.state.kind === 'success') {
        URL.revokeObjectURL(preview.state.objectUrl);
      }
    };
  }, [preview]);

  const hasInFlightUpload = localUploads.some(
    (u) => u.status.state === 'uploading'
  );
  const hasInFlightDelete = deletingIds.size > 0;
  const canConfirm =
    infos.uploadedCount > 0 &&
    !hasInFlightUpload &&
    !hasInFlightDelete &&
    !isConfirming;

  /**
   * Pipeline d'un fichier individuel : validation locale taille → entrée
   * "uploading" dans la liste locale → appel back → succès = on retire la
   * ligne (le fichier est désormais dans `infos.uploadedDocuments`), erreur
   * = on remplace par un statut erreur.
   */
  const handleFile = useCallback(
    async (file: File) => {
      const id = nextUploadId();

      if (file.size > DEPOT_MAX_FILE_SIZE_BYTES) {
        setLocalUploads((prev) => [
          ...prev,
          {
            id,
            fileName: file.name,
            size: file.size,
            status: {
              state: 'error',
              message: `Fichier trop volumineux (max ${MAX_FILE_SIZE_LABEL}).`
            }
          }
        ]);
        return;
      }

      setLocalUploads((prev) => [
        ...prev,
        {
          id,
          fileName: file.name,
          size: file.size,
          status: { state: 'uploading' }
        }
      ]);

      try {
        await onUploadFile(file);
        // Succès : on retire la ligne — la version persistante apparaît
        // dans le bloc "documents reçus" via `infos.uploadedDocuments`.
        setLocalUploads((prev) => prev.filter((u) => u.id !== id));
      } catch (e) {
        // Pour les erreurs métier on passe par le formateur qui consolide
        // les 4 codes de format en un seul message lisible, traduit le
        // FileTooLarge avec la bonne limite, etc.
        const message =
          e instanceof DepotBusinessError
            ? formatDepotErrorMessage(e)
            : e instanceof Error
              ? e.message
              : 'Envoi impossible. Veuillez réessayer.';
        setLocalUploads((prev) =>
          prev.map((u) =>
            u.id === id ? { ...u, status: { state: 'error', message } } : u
          )
        );
      }
    },
    [onUploadFile]
  );

  /**
   * On accepte plusieurs fichiers à la fois (drop multi-fichiers ou sélection
   * Cmd+clic) : chaque fichier est uploadé en parallèle, gestion d'état par
   * fichier. Le back fera les rejets éventuels (taille, format, expiration…).
   */
  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      Array.from(files).forEach(handleFile);
    },
    [handleFile]
  );

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDelete = useCallback(
    async (storedFileId: string) => {
      setDeletingIds((prev) => new Set(prev).add(storedFileId));
      try {
        await onDeleteFile(storedFileId);
      } catch {
        // L'erreur a été remontée par la page (toast / écran terminal selon
        // le code). Rien à faire ici de plus que désactiver le loading.
      } finally {
        setDeletingIds((prev) => {
          const next = new Set(prev);
          next.delete(storedFileId);
          return next;
        });
      }
    },
    [onDeleteFile]
  );

  const dismissLocalError = useCallback((id: string) => {
    setLocalUploads((prev) => prev.filter((u) => u.id !== id));
  }, []);

  /**
   * Ouverture de la modale de prévisualisation : on positionne d'abord
   * l'état "loading" pour avoir un affichage immédiat, puis on lance le
   * fetch. Si une autre prévisu était en cours, son blob est révoqué via
   * l'effet de cleanup déclenché par le changement de state.
   */
  const handlePreview = useCallback(
    async (doc: DepotUploadedDocument) => {
      setPreview({ doc, state: { kind: 'loading' } });
      try {
        const { blob, contentType } = await onPreviewFile(doc.storedFileId);
        const objectUrl = URL.createObjectURL(blob);
        setPreview({
          doc,
          state: { kind: 'success', objectUrl, contentType }
        });
      } catch (e) {
        const message =
          e instanceof DepotBusinessError
            ? formatDepotErrorMessage(e)
            : e instanceof Error
              ? e.message
              : 'Impossible d’afficher ce document. Veuillez réessayer.';
        setPreview({ doc, state: { kind: 'error', message } });
      }
    },
    [onPreviewFile]
  );

  const handleClosePreview = useCallback(() => {
    // Révocation explicite ici aussi (en plus de l'effet) — utile si le
    // composant n'est pas démonté mais que l'utilisateur ferme la modale.
    setPreview((current) => {
      if (current?.state.kind === 'success') {
        URL.revokeObjectURL(current.state.objectUrl);
      }
      return null;
    });
  }, []);

  return (
    <DepotLayout branding={branding}>
      <div className="flex flex-col gap-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">
              Dépôt sécurisé — {infos.displayName}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Déposez ici les documents médicaux demandés par votre courtier.
              Vous pouvez ajouter ou retirer des fichiers et revenir plus
              tard si besoin (lien valable 30 jours).
            </p>
          </div>
        </div>

        {/* Liste des documents attendus — purement informative, on n'attache pas
            de statut "par document" parce que le back agrège tout dans
            uploadedCount sans mapper aux libellés. */}
        {infos.requiredDocuments.length > 0 && (
          <div className="rounded-lg bg-gray-50 border border-gray-200 p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Documents demandés :
            </p>
            <ul className="flex flex-col gap-1 text-sm text-gray-600 list-disc list-inside">
              {infos.requiredDocuments.map((doc, idx) => (
                <li key={`${idx}-${doc}`}>{doc}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Zone drag-and-drop. On garde l'input file caché : on l'ouvre depuis
            le clic sur la zone — UX standard, accessible au clavier (label). */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition ${
            isDragOver
              ? 'border-primary bg-primary/5'
              : 'border-gray-300 hover:border-primary/60 hover:bg-gray-50'
          }`}
        >
          <UploadCloud className="w-10 h-10 mx-auto text-primary mb-3" />
          <p className="text-base font-medium text-gray-700">
            Glissez vos fichiers ici, ou cliquez pour parcourir
          </p>
          <p className="text-xs text-gray-500 mt-1">
            PDF, JPG, PNG — {MAX_FILE_SIZE_LABEL} max par fichier
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_FORMATS}
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              // Reset pour permettre de re-sélectionner le même fichier après
              // une erreur (sinon onChange ne refire pas).
              e.target.value = '';
            }}
          />
        </div>

        {/* Liste persistante des fichiers déjà reçus par le back. C'est la
            source de vérité — survit aux refresh, peut être éditée via le
            bouton de suppression. */}
        {infos.uploadedDocuments.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-700">
                Documents reçus
              </p>
              <span className="text-xs font-medium text-primary">
                {infos.uploadedDocuments.length}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {infos.uploadedDocuments.map((doc) => (
                <UploadedRow
                  key={doc.storedFileId}
                  doc={doc}
                  isDeleting={deletingIds.has(doc.storedFileId)}
                  disableActions={isConfirming || hasInFlightUpload}
                  onDelete={() => handleDelete(doc.storedFileId)}
                  onPreview={() => handlePreview(doc)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Uploads en cours / rejetés (transient, scope = onglet). */}
        {localUploads.length > 0 && (
          <div className="flex flex-col gap-2">
            {localUploads.map((u) => (
              <LocalUploadRow
                key={u.id}
                entry={u}
                onDismiss={() => dismissLocalError(u.id)}
              />
            ))}
          </div>
        )}

        {confirmErrorMessage && (
          <div
            role="alert"
            className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
          >
            {confirmErrorMessage}
          </div>
        )}

        <div className="flex flex-col gap-2 border-t pt-5">
          <button
            type="button"
            disabled={!canConfirm}
            onClick={onConfirm}
            className="inline-flex items-center justify-center gap-2 w-full rounded-lg bg-primary px-4 py-3 text-white font-medium hover:bg-primary/90 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isConfirming && <Loader2 className="w-4 h-4 animate-spin" />}
            {isConfirming ? 'Confirmation…' : "J'ai terminé"}
          </button>
          {infos.uploadedCount === 0 && (
            <p className="text-xs text-center text-gray-500">
              Ajoutez au moins un fichier avant de pouvoir confirmer.
            </p>
          )}
        </div>
      </div>

      {preview && (
        <DepotDocumentPreview
          doc={preview.doc}
          state={preview.state}
          onClose={handleClosePreview}
        />
      )}
    </DepotLayout>
  );
};

/**
 * Ligne d'un document persistant côté back. Affichage compact : nom +
 * date d'upload + boutons œil (prévisualisation) et corbeille (suppression).
 *
 * Le bouton œil est toujours actif (la prévisu ne modifie pas l'état) ;
 * la corbeille est bloquée pendant les opérations qui pourraient causer
 * une race (upload en cours, confirmation en cours).
 */
const UploadedRow: React.FC<{
  doc: DepotUploadedDocument;
  isDeleting: boolean;
  disableActions: boolean;
  onDelete: () => void;
  onPreview: () => void;
}> = ({ doc, isDeleting, disableActions, onDelete, onPreview }) => (
  <div className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2">
    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-700 truncate">
        {doc.fileName}
      </p>
      <p className="text-xs text-gray-500">
        Déposé le {formatUploadedAt(doc.uploadedAt)}
      </p>
    </div>
    <button
      type="button"
      onClick={onPreview}
      aria-label={`Voir l’aperçu de ${doc.fileName}`}
      className="flex-shrink-0 p-1.5 rounded hover:bg-primary/10 text-gray-400 hover:text-primary transition"
    >
      <Eye className="w-4 h-4" />
    </button>
    <button
      type="button"
      onClick={onDelete}
      disabled={isDeleting || disableActions}
      aria-label={`Supprimer ${doc.fileName}`}
      className="flex-shrink-0 p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
    >
      {isDeleting ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Trash2 className="w-4 h-4" />
      )}
    </button>
  </div>
);

/**
 * Ligne éphémère pour les fichiers en cours d'upload ou rejetés. Les
 * erreurs offrent un bouton de dismiss pour ne pas encombrer la zone si
 * l'utilisateur a déjà compris.
 */
const LocalUploadRow: React.FC<{
  entry: LocalUploadEntry;
  onDismiss: () => void;
}> = ({ entry, onDismiss }) => {
  const { fileName, size, status } = entry;
  const isError = status.state === 'error';

  return (
    <div
      className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
        isError ? 'border-red-200 bg-red-50' : 'border-gray-200'
      }`}
    >
      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-700 truncate">
          {fileName}
        </p>
        <p
          className={`text-xs ${
            isError ? 'text-red-600' : 'text-gray-500'
          }`}
        >
          {formatBytes(size)} ·{' '}
          {status.state === 'uploading' ? 'Envoi en cours…' : status.message}
        </p>
      </div>
      <div className="flex-shrink-0">
        {status.state === 'uploading' ? (
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
        ) : (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Ignorer cette erreur"
            className="p-1 text-red-500 hover:text-red-700"
          >
            <AlertCircle className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};
